import FromToTable from "../components/quote-pdf/FromToTable";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import useGet from "../hooks/useGet";
import LoadingOverlay from "../components/shared/LoadingOverlay";
import ErrorModal from "../components/shared/ErrorModal";
import { NumericFormat } from "react-number-format";
import getDenomination from "../utilities/getDenomination";
import poweredByContext from "../store/PoweredByContext";
import { useGetCacheContent } from "../hooks/useGetCacheContent";
import format from "date-fns/format";
import parse from "date-fns/parse";
import { getMeasurements } from "../utilities/string";
import { isFloatingFleet } from "../utilities/aircraft";
import { getScheduleDisclaimer } from "../utilities/scheduleDisclaimer";
// import { getTripTypeLabelFromLegCount, normalizeTripTypeDisplay } from "../utilities/tripTypeDisplay";

// Icons
import shyLogo from "../assets/icons/shy-logo-quote-pdf.svg";
import cateringIcon from "../assets/icons/catering-alt-dark.svg";
import wifiIcon from "../assets/icons/wifi-dark.svg";
import wcIcon from "../assets/icons/toilet-dark.svg";
import luggageIcon from "../assets/icons/luggage-dark.svg";
import smokingIcon from "../assets/icons/smoking-dark.svg";
import attendantsIcon from "../assets/icons/cabin-crew-dark.svg";
import petsIcon from "../assets/icons/pets-dark.svg";
import subjectToWeightIcon from "../assets/icons/subject-to-weight-dark.svg";
import subjectToConditionsIcon from "../assets/icons/subject-to-conditions-dark.svg";
import ownerApprovalIcon from "../assets/icons/owner-approval-dark.svg";
import movidaLogo from "../assets/icons/movida-logo.png";
import bedIcon from "../assets/icons/bed-dark.svg";
import pilotsIcon from "../assets/icons/pilots-dark.svg";
import co2Icon from "../assets/icons/co2.svg";
import starIcon from "../assets/icons/star-dark.svg";

const formatPets = (content) => {
    if (content.pets !== undefined && content.pets !== null) return String(content.pets);
    if (content.numberOfPets !== undefined && content.numberOfPets !== null) return String(content.numberOfPets);
    return "0";
};

const showPets = (content) => {
    const p = content.pets !== undefined && content.pets !== null ? content.pets : null;
    const n = content.numberOfPets !== undefined && content.numberOfPets !== null ? content.numberOfPets : null;
    return (p != null && p !== 0) || (n != null && n !== 0);
};

const showBed = (content) => {
    const bed = content.bed;
    if (bed == null || bed === "") return false;
    const v = String(bed).trim().toLowerCase();
    if (v === "no" || v === "n" || v === "0") return false;
    if (bed === 0) return false;
    return v === "yes" || v === "y" || v === "1" || bed === 1;
};

const QuotePdf = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [aircraftCount, setAircraftCount] = useState(0);

    const poweredByCtx = useContext(poweredByContext);

    const { content: homeContent } = useGetCacheContent("homeContent", "/api/content/home");

    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const legId = queryParams.get("lo");
    const name = queryParams.get("name");

    const { error: aircraftOptionsError, returnData: aircraftOptions } = useGet(
        `/api/frontend/v2/get-search-results?LegId=${legId}`
    );
    const { error: tripInfoError, returnData: tripInfo } = useGet(
        `/api/frontend/get-search-results-header?LegId=${legId}`
    );

    useEffect(() => {
        if (aircraftOptionsError || tripInfoError) {
            setError(true);
        } else {
            if (aircraftOptions && tripInfo) {
                setLoading(false);
                setAircraftCount(aircraftOptions?.length);
            }
        }
    }, [aircraftOptionsError, tripInfoError, aircraftOptions, tripInfo]);

    // Add PDF view class to body
    useEffect(() => {
        document.body.classList.add("pdf-view", "pdf-view--hide-header");

        // Clean up PDF view
        return () => {
            document.body.classList.remove("pdf-view", "pdf-view--hide-header");
        };
    });

    const getPhoneNumber = () => {
        const tenantPhone = poweredByCtx?.tenant?.tenantPhoneNumber;
        if (poweredByCtx?.isPoweredBy && tenantPhone) {
            return tenantPhone.split(";").map((number) => number.trim());
        }
        return [homeContent?.getInTouch?.phoneNumber];
    };

    const getLogo = (recommended) => {
        let image;

        if (poweredByCtx?.isPoweredBy) {
            try {
                image = poweredByCtx?.tenant?.logoImage;
                if (image) {
                    return image;
                } else {
                    console.log(`Logo image not available for ${poweredByCtx.tenantID}`);
                }
            } catch (e) {
                console.log(`Error fetching logo for ${poweredByCtx.tenantID}: ${e.message}`);
            }
        }

        return shyLogo;
    };

    const pbsOverride = poweredByCtx?.isPoweredBy;

    const getMovidaPoints = (price) => {
        return Math.floor((price.gbp / 100) * 1);
    };

    const getCrossSectionImage = (images) => {
        if (images?.length > 1) {
            return images[images.length - 1];
        }
    };

    const hasManyLegs = Array.isArray(tripInfo?.legs) && tripInfo.legs.length > 3;

    return (
        <div className="quote-pdf">
            {error && <ErrorModal errorMessage="PDF Could not be created" />}
            {loading && <LoadingOverlay clear />}
            {!loading && tripInfo && aircraftOptions && (
                <>
                    {aircraftOptions.map((aircraft, index) => {
                        const simplifiedDate = tripInfo.quotesreleaseDate.substring(0, 10);
                        const parseDate = parse(simplifiedDate, "yyyy-MM-dd", new Date());
                        const formattedDate = format(parseDate, "dd/MM/yy", { awareOfUnicodeTokens: true });

                        let dimensionsClass = "";
                        if (!aircraft.floorPlan[2]) {
                            dimensionsClass = "quote-pdf__dimensions--no-cross-section";
                        }

                        let smoking = aircraft.smokingOption;
                        if (aircraft.smokingOption === "N") {
                            smoking = "No";
                        } else if (aircraft.smokingOption === "Y") {
                            smoking = "Yes";
                        }

                        const crosSectionImage = getCrossSectionImage(aircraft.floorPlan);
                        const clientDisplayName = (name || "").trim();

                        // const quotePdfTripTypeLabel =
                        //     normalizeTripTypeDisplay(tripInfo.tripType) ||
                        //     getTripTypeLabelFromLegCount(tripInfo.legs?.length);

                        const scheduleSection = (
                            <div
                                className={
                                    hasManyLegs
                                        ? "quote-pdf__schedule quote-pdf__schedule--after-details"
                                        : "quote-pdf__schedule"
                                }
                            >
                                <FromToTable
                                    departureDateTime={aircraft.departureDateTime}
                                    arrivalDateTime={aircraft.arrivalDateTime}
                                    legData={tripInfo.legs}
                                    duration={aircraft.duration}
                                    fuelStops={aircraft.fuelStops}
                                />
                                <p className="quote-pdf__schedule-disclaimer">
                                    <em>{getScheduleDisclaimer(aircraft.ownerApproval)}</em>
                                </p>
                            </div>
                        );

                        return (
                            <div key={aircraft.id} className="quote-pdf__page">
                                <header className={`quote-pdf__header ${pbsOverride && "quote-pdf__header--pbs"}`}>
                                    <div className="quote-pdf__header-client">{clientDisplayName || "-"}</div>
                                    <div
                                        className={`quote-pdf__header-right${
                                            aircraft.recommended ? " quote-pdf__header-right--with-recommends" : ""
                                        }`}
                                    >
                                        <div className="quote-pdf__header-quote">
                                            Quote {index + 1} <span className="quote-pdf__lowercase">of</span>{" "}
                                            {aircraftCount}
                                        </div>
                                        {aircraft.recommended && (
                                            <div className="quote-pdf__header-recommends">
                                                <img src={starIcon} alt="" />
                                                <span>{pbsOverride ? "Recommended" : "SHY Recommends"}</span>
                                            </div>
                                        )}
                                    </div>
                                    <img className="quote-pdf__logo" alt="" src={getLogo(aircraft.recommended)} />
                                </header>

                                <table className="quote-pdf__header-table">
                                    <tbody>
                                        <tr>
                                            <td colSpan="2">
                                                <div className="quote-pdf__header-meta-inline">
                                                    <div className="quote-pdf__header-meta-item">
                                                        <span>Date:</span>
                                                        <span>{formattedDate}</span>
                                                    </div>
                                                    {!pbsOverride && (
                                                        <div className="quote-pdf__header-meta-item">
                                                            <span>Agent:</span>
                                                            <span>{tripInfo.agentName}</span>
                                                        </div>
                                                    )}
                                                    <div className="quote-pdf__header-meta-item">
                                                        <span>Team:</span>
                                                        <span>
                                                            {getPhoneNumber().map((phoneNumber, i) => {
                                                                return (
                                                                    <a key={i} href={`tel:${phoneNumber}`}>
                                                                        {phoneNumber}
                                                                    </a>
                                                                );
                                                            })}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div className="quote-pdf__aircraft-images">
                                    {aircraft.photoData[0] && (
                                        <img src={aircraft.photoData[0].url} alt={aircraft.photoData[0].alt} />
                                    )}
                                    {aircraft.photoData[1] && (
                                        <img src={aircraft.photoData[1].url} alt={aircraft.photoData[1].alt} />
                                    )}
                                </div>

                                {!hasManyLegs && scheduleSection}

                                <div className="quote-pdf__aircraft-details">
                                    <div className="quote-pdf__aircraft-details-column">
                                        <h2>Aircraft</h2>
                                        <table className="quote-pdf__details-table">
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <span>{aircraft.aircraftName},</span>
                                                        <span>
                                                            {aircraft.category}
                                                            {isFloatingFleet(aircraft) && (
                                                                <span> / *Floating Fleet</span>
                                                            )}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span>Manufactured:</span>
                                                        <span>
                                                            {aircraft.yom}
                                                            {aircraft.refurbishedYear !== ""
                                                                ? ` / Refurbished: ${aircraft.refurbishedYear}`
                                                                : ``}
                                                        </span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <span>Seating capacity:</span>
                                                        <span>Up to {aircraft.seats} passengers</span>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>

                                        <div className={`quote-pdf__dimensions ${dimensionsClass}`}>
                                            {aircraft.floorPlan[0] && (
                                                <img
                                                    className="quote-pdf__dimensions-layout-image"
                                                    src={aircraft.floorPlan[0].url}
                                                    alt={aircraft.floorPlan[0].alt}
                                                />
                                            )}

                                            <div className="quote-pdf__dimensions-length">
                                                <span>Length:</span>
                                                <span>{getMeasurements(aircraft.cabinLength)}</span>
                                            </div>

                                            <div className="quote-pdf__dimensions-width">
                                                <span>Width:</span>
                                                <span>{getMeasurements(aircraft.cabinWidth)}</span>
                                            </div>

                                            <div className="quote-pdf__dimensions-height">
                                                <span>Height:</span>
                                                <span>{getMeasurements(aircraft.headRoom)}</span>
                                            </div>

                                            {crosSectionImage && (
                                                <img
                                                    className="quote-pdf__dimensions-cross-section-image"
                                                    src={crosSectionImage.url}
                                                    alt={crosSectionImage.alt || ""}
                                                />
                                            )}
                                        </div>

                                        {isFloatingFleet(aircraft) && (
                                            <p className="quote-pdf__floating-fleet">
                                                <em>
                                                    * Floating fleet is a supplier with multiple of the same OR similar
                                                    category of aircraft (Interior and exterior aesthetics are subject
                                                    to change)
                                                </em>
                                            </p>
                                        )}
                                        {aircraft.notes && aircraft.notes.trim().length > 0 && (
                                            <>
                                                <h2>Notes</h2>
                                                <p>{aircraft.notes}</p>
                                            </>
                                        )}
                                    </div>

                                    <div className="quote-pdf__aircraft-details-dividing-line"></div>

                                    <div>
                                        <div className="quote-pdf__aircraft-details-column">
                                            <h2>FEATURES</h2>

                                            <table className="quote-pdf__details-table">
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <img src={luggageIcon} alt="" />
                                                            <span>Luggage</span>
                                                            <span>
                                                                - {aircraft.luggage} Bag
                                                                {aircraft.luggage === 1 ? "" : "s"} (Max 20kg Each)
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={cateringIcon} alt="" />
                                                            <span>Catering</span>
                                                            <span>- {aircraft.cateringOption}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={wcIcon} alt="" />
                                                            <span>WC</span>
                                                            <span>- {aircraft.toilet}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={ownerApprovalIcon} alt="" />
                                                            <span>Owner Approval</span>
                                                            <span>
                                                                -{" "}
                                                                {aircraft.ownerApproval === "Y"
                                                                    ? "Required"
                                                                    : "Not Required"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={wifiIcon} alt="" />
                                                            <span>WiFi</span>
                                                            <span>- {aircraft.wifiOption}</span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={pilotsIcon} alt="" />
                                                            <span>Pilots</span>
                                                            <span>
                                                                -{" "}
                                                                {aircraft.numberOfPilots !== undefined &&
                                                                aircraft.numberOfPilots !== null
                                                                    ? aircraft.numberOfPilots
                                                                    : "-"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <img src={attendantsIcon} alt="" />
                                                            <span>Flight Attendant</span>
                                                            <span>
                                                                - {aircraft.flightAttendants === "Y" ? "Yes" : "No"}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    {aircraft.smokingOption === "Y" && (
                                                        <tr>
                                                            <td>
                                                                <img src={smokingIcon} alt="" />
                                                                <span>Smoking</span>
                                                                <span>- {smoking}</span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {showPets(aircraft) && (
                                                        <tr>
                                                            <td>
                                                                <img src={petsIcon} alt="" />
                                                                <span>Pets</span>
                                                                <span>- {formatPets(aircraft)}</span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {showBed(aircraft) && (
                                                        <tr>
                                                            <td>
                                                                <img src={bedIcon} alt="" />
                                                                <span>Bed</span>
                                                                <span>- {aircraft.bed}</span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {aircraft.co2 !== undefined && aircraft.co2 !== null && (
                                                        <tr>
                                                            <td>
                                                                <img src={co2Icon} alt="" />
                                                                <span>CO₂</span>
                                                                <span>- {`${Number(aircraft.co2).toFixed(2)}T`}</span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {aircraft.subjectToWeight === "Y" && (
                                                        <tr>
                                                            <td>
                                                                <img src={subjectToWeightIcon} alt="" />
                                                                <span>
                                                                    <em>Subject to Weight</em>
                                                                </span>
                                                                <span>
                                                                    - <em>Yes</em>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {aircraft.subjectToConditions !== "None" && (
                                                        <tr>
                                                            <td>
                                                                <img src={subjectToConditionsIcon} alt="" />
                                                                <span>
                                                                    <em>Subject to Conditions</em>
                                                                </span>
                                                                <span>
                                                                    - <em>{aircraft.subjectToConditions}</em>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {hasManyLegs && scheduleSection}

                                <div className={`quote-pdf__footer ${pbsOverride && "quote-pdf__footer--pbs"}`}>
                                    <div className="quote-pdf__footer-column">
                                        {tripInfo.showMovida && (
                                            <div className="quote-pdf__movida-info">
                                                <div className="quote-pdf__movida-info-row">
                                                    <div className="quote-pdf__movida-info-brand">
                                                        <img
                                                            src={movidaLogo}
                                                            alt="Movida"
                                                            className="quote-pdf__movida-logo"
                                                        />
                                                        <span className="quote-pdf__movida-info-label">
                                                            MOVIDA POINTS
                                                        </span>
                                                    </div>
                                                    <span className="quote-pdf__movida-info-points">
                                                        {getMovidaPoints(aircraft.price)}
                                                    </span>
                                                </div>
                                                <div className="quote-pdf__movida-info-text">
                                                    This booking can earn you {getMovidaPoints(aircraft.price)} MOVIDA
                                                    points
                                                </div>
                                            </div>
                                        )}

                                        <div className="quote-pdf__legal-info">
                                            Price quoted is subject to change and may incur additional costs. For more
                                            information, please refer to our{" "}
                                            <a
                                                href={
                                                    tripInfo.showMovida
                                                        ? "/movida-terms-conditions"
                                                        : "/terms-conditions"
                                                }
                                            >
                                                Terms and Conditions
                                            </a>
                                            .
                                        </div>
                                    </div>

                                    <div className="quote-pdf__footer-column">
                                        <div className="quote-pdf__footer-price-block">
                                            <div className="quote-pdf__price">
                                                <span className="quote-pdf__price-amount">
                                                    {getDenomination(aircraft.preferredCurrency)}
                                                    <NumericFormat
                                                        displayType="text"
                                                        value={aircraft.price[aircraft.preferredCurrency.toLowerCase()]}
                                                        thousandSeparator
                                                        decimalScale={0}
                                                    />
                                                    &nbsp;{aircraft.preferredCurrency.toUpperCase()}
                                                </span>
                                            </div>
                                            <a
                                                href={`/book?lo=${legId}&arfq=${aircraft.id}&tailNum=${aircraft.tailNumber}&name=${name}`}
                                                className="quote-pdf__book-now-cta"
                                            >
                                                RESERVE NOW
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
};
export default QuotePdf;
